<script lang="ts" setup>
	import type { ProductPlatformKeys } from '~/server/type'
	import StringMethods from '~/utils/character'

	const { platform = 'business', ...props } = defineProps<{
		platform?: ProductPlatformKeys
		productName?: string
		productCode?: number
		productCategory?: string
		title: string
		description?: string
		sort?: boolean
		exclude?: (string | number)[]
		underwriter?: string
	}>()

	const { toLower } = StringMethods()
	const { getProductsByCategory, getProductsByProductName } = useProductLogic(platform)
	const wrapperEl = useTemplateRef('benefitListDisplay')
	const { isInView } = useInternalScrollView(wrapperEl)
	const selectedProduct = computed(() => {
		const collection = getProductsByProductName.value?.[toLower(props.productName)] ?? null
		if (collection?.list && collection.list?.length > 1) {
			return collection
		} else if (props.productCategory) {
			const productList = getProductsByCategory.value[toLower(props.productCategory)]
			if (productList) {
				const findDefaultProduct = productList.find(item => item.code_name === props.productCode)
				return {
					product_name: findDefaultProduct?.name ?? '',
					category: toLower(findDefaultProduct?.productCategory?.name ?? ''),
					code_name: findDefaultProduct?.code_name,
					group_id: findDefaultProduct?.group_id,
					list: productList
				}
			}
		}
	})
	const selectedProductList = computed(() => {
		if (Array.isArray(selectedProduct.value?.list)) {
			return selectedProduct.value?.list.filter(item => !props.exclude?.includes(item.code_name))
		}
		return []
	})
</script>

<template>
	<div class="flex-col items-center" ref="benefitListDisplay">
		<ui-title-header
			:title="title"
			:ui="{
				size: 'sm:text-[3.25em] text-[2em]',
				lineHeight: 'sm:leading-[62.4px] leading-[38.4px]'
			}"
			class="text-center mx-auto max-w-[938px] w-full"
		/>
		<p class="text-gray-600 md:text-xl mt-8 text-center w-full mx-auto" v-html="description" />
		<transition
			name="fade"
			enter-active-class="animate__animated animate__fadeIn"
			leave-active-class="animate__animated animate__fadeOut"
		>
			<molecules-benefit-skin
				v-if="selectedProduct?.code_name && isInView"
				class="mt-20 max-h-[calc(100vh-5rem)] invisible-scrollbar overflow-y-auto"
				:product-code="productCode || selectedProduct?.code_name || 0"
				:product-group-id="selectedProduct?.group_id || ''"
				:product-name="selectedProduct?.product_name || ''"
				:product-category="selectedProduct?.category || productCategory || ''"
				:product-list="selectedProductList"
				:provider="underwriter || ''"
				:sort="sort"
			/>
		</transition>
	</div>
</template>
